import type { CsmLeaderboardCompanyRow } from '../../../api/dashboard';
import { formatCompact } from '../../../utils/numberFormat';
import { formatPctChange } from './pctChange';

/** Logins up at least this much… */
export const LOGIN_INFLATION_EVENTS_PCT = 100;
/** …while active users grew less than this reads as token refreshes, not more use. */
export const LOGIN_INFLATION_USERS_PCT = 20;

/**
 * Pure: the warning shown on the Classic login-events tile when logins jumped
 * without the users to match (12 Oct 2026: +178% login events, active users
 * −12%, top users ">10×"), or null. A burst of Auth0 silent token refreshes
 * multiplies login events for a week or two with nobody using the product
 * more. Exported for tests.
 */
export function loginInflationNote(eventsDeltaPct: number | null, activeUsersDeltaPct: number | null): string | null {
  if (eventsDeltaPct === null || eventsDeltaPct < LOGIN_INFLATION_EVENTS_PCT) return null;
  if (activeUsersDeltaPct !== null && activeUsersDeltaPct >= LOGIN_INFLATION_USERS_PCT) return null;
  const users =
    activeUsersDeltaPct === null
      ? 'no matching rise in active users'
      : `active users ${formatPctChange(activeUsersDeltaPct)}`;
  return `Likely inflated: ${users}. Token refreshes, not more use; read Active users instead.`;
}

export const signedPct = formatPctChange;

/**
 * Pure: "Classic + aOS 89.9M, +4%", the combined figure At risk compares, shown
 * under a Classic-only leaderboard row so its Classic change (OpenWay −60%) is
 * never read as the company's whole trend. Exported for tests.
 */
export function combinedLine(
  combined: CsmLeaderboardCompanyRow['combined'],
  error: string | null | undefined,
): string | undefined {
  if (error) return 'Classic + aOS unavailable';
  if (combined === undefined) return undefined;
  if (combined === null) return 'Classic + aOS unavailable';
  const change = combined.deltaPct === null ? 'no change: previous period not covered' : signedPct(combined.deltaPct);
  return `Classic + aOS ${formatCompact(combined.tokens) || '0'}, ${change}`;
}

/**
 * When a platform company is one of several workspaces behind one CSM-list
 * entry, Usage counts the workspace and Customers counts the entry: ISS
 * Facility Services UK is 19/32 active here and 19/45 on Customers, which adds
 * an idle "ISS" workspace (13 members). Says so, so the two numbers reconcile.
 * Null for a single-workspace company. Exported for tests.
 */
export function workspaceShareNote(
  companyId: string,
  roster: ReadonlyArray<{
    companyName: string | null;
    matchedProdCompanyIds: readonly string[];
    activeMembers30d: number;
    totalMembers: number;
  }>,
): string | null {
  const entry = roster.find((r) => r.matchedProdCompanyIds.length > 1 && r.matchedProdCompanyIds.includes(companyId));
  if (!entry) return null;
  return `1 of ${entry.matchedProdCompanyIds.length} workspaces: ${entry.activeMembers30d.toLocaleString('en-US')}/${entry.totalMembers.toLocaleString('en-US')} active on Customers`;
}
